import { FetchResult, MutationFunction } from '@apollo/client'
import { ImpressaoAtendimentoInput } from 'api/ImpressaoAtendimentoIndividual'
import { useAlert } from 'components/alert'
import { TipoAtendimentoProfissional } from 'graphql/types.generated'
import { useFirebase } from 'hooks/firebase/useFirebase'
import { useCallback } from 'react'
import {
  RetificacaoInput,
  RetificarMutations,
  tipoAtendimentoRetificacaoRecord,
} from 'view/retificacao-atendimento/model-retificacaoAtendimento'
import {
  isRetificarAtendimentoMutation,
  isRetificarAtendimentoVacinacaoMutation,
} from 'view/retificacao-atendimento/util-retificacaoAtendimento'

interface UseAtendimentoHandleRetificarProps<RetificarMutationResult extends RetificarMutations> {
  readonly tipoAtendimentoProfissional: TipoAtendimentoProfissional
  readonly printItem: (itemToPrint: ImpressaoAtendimentoInput) => void
  readonly onFinalizar: MutationFunction<RetificarMutationResult, { input: RetificacaoInput }>
  readonly goToCallbackUrl: () => void
}

interface UseAtendimentoHandleRetificarResult {
  handleRetificar: (input: RetificacaoInput, imprimirAtendimento: boolean) => Promise<FetchResult<RetificarMutations>>
}

export function useAtendimentoHandleRetificar<RetificarMutationResult extends RetificarMutations>(
  props: UseAtendimentoHandleRetificarProps<RetificarMutationResult>
): UseAtendimentoHandleRetificarResult {
  const { tipoAtendimentoProfissional, printItem, onFinalizar, goToCallbackUrl } = props

  const alert = useAlert()
  const { analytics } = useFirebase()

  const handleRetificar = useCallback(
    async (input: RetificacaoInput, imprimirAtendimento: boolean) => {
      const response = await onFinalizar({
        variables: {
          input,
        },
      })

      if (imprimirAtendimento) {
        if (isRetificarAtendimentoMutation(response.data)) {
          printItem({ atendimentoProfissionalId: response.data.retificarAtendimento })
        } else if (isRetificarAtendimentoVacinacaoMutation(response.data)) {
          printItem({ atendimentoProfissionalId: response.data.retificarAtendimentoVacinacao })
        }
      }

      const nomeAtendimento = tipoAtendimentoRetificacaoRecord[tipoAtendimentoProfissional].title

      alert('success', `${nomeAtendimento.capitalize()} retificado com sucesso.`)
      analytics.logEvent('salvar_RET', { tipo_de_atendimento: nomeAtendimento })
      goToCallbackUrl()

      return response
    },
    [onFinalizar, printItem, tipoAtendimentoProfissional, alert, analytics, goToCallbackUrl]
  )

  return {
    handleRetificar,
  }
}
